

import React, { useState, useEffect } from "react";
import DashboardLayout from "./DashboardLayout";
import "react-quill/dist/quill.snow.css";
import ReactQuill from "react-quill";
import axios from "axios";

import BlogManagement from "./BlogManagement";
import SubscribersList from "./SubscribersList";

const PostBlog = () => {
  
  const [activeTab, setActiveTab] = useState("createPost"); // 'createPost', 'management', 'subscribers'
  
  
  const [formData, setFormData] = useState({
    authorName: "",
    title: "",
    metaTitle: "",
    metaDescription: "",
    category: "",
    content: "",
    image: "",
    slug: "",
    keywords: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftExists, setDraftExists] = useState(false);

  // Load draft from localStorage when component mounts
  useEffect(() => {
    const savedDraft = localStorage.getItem('blogDraft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        setFormData(prevData => ({
          ...prevData,
          authorName: draftData.authorName || "",
          title: draftData.title || "",
          metaTitle: draftData.metaTitle || "",
          metaDescription: draftData.metaDescription || "",
          category: draftData.category || "",
          content: draftData.content || "",
          slug: draftData.slug || "",
          keywords: draftData.keywords || "",
        }));
        
        // Set word count for loaded content
        if (draftData.content) {
          setWordCount(
            draftData.content
              .replace(/<[^>]*>/g, "")
              .trim()
              .split(/\s+/).length
          );
        }
        
        setDraftExists(true);
      } catch (error) {
        console.error("Error loading draft:", error);
      }
    }
  }, []);

  // Save draft to localStorage whenever formData changes
  useEffect(() => {
    // Don't save if form is completely empty
    const isFormEmpty = 
      !formData.authorName && 
      !formData.title && 
      !formData.metaTitle && 
      !formData.metaDescription && 
      !formData.category && 
      !formData.content && 
      !formData.slug && 
      !formData.keywords;
      
    if (!isFormEmpty) {
      const draftData = {
        authorName: formData.authorName,
        title: formData.title,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        category: formData.category,
        content: formData.content,
        slug: formData.slug,
        keywords: formData.keywords,
        
      };
      
      localStorage.setItem('blogDraft', JSON.stringify(draftData));
      setDraftSaved(true);
      setDraftExists(true);
      
      // Reset the "Draft Saved" message 
      setTimeout(() => {
        setDraftSaved(false);
      }, 3000);
    }
  }, [formData]);

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/ /g, "-")
      .replace(/[^a-z0-9-]/g, "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "title") {
      setFormData({ ...formData, title: value, slug: generateSlug(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleContentChange = (value) => {
    setFormData({ ...formData, content: value });
    setWordCount(
      value
        .replace(/<[^>]*>/g, "")
        .trim()
        .split(/\s+/).length
    );
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const clearDraft = () => {
    localStorage.removeItem('blogDraft');
    setDraftExists(false);
    setFormData({
      authorName: "",
      title: "",
      metaTitle: "",
      metaDescription: "",
      category: "",
      content: "",
      image: "",
      slug: "",
      keywords: "",
    });
    setImagePreview(null);
    setWordCount(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.authorName ||
      !formData.title ||
      !formData.category ||
      !formData.content
    ) {
      alert("Please fill in all required fields!");
      return;
    }

    setLoading(true);

    const blogData = new FormData();
    blogData.append("authorName", formData.authorName);
    blogData.append("title", formData.title);
    blogData.append("metaTitle", formData.metaTitle || "");
    blogData.append("metaDescription", formData.metaDescription || "");
    blogData.append("category", formData.category);
    blogData.append("slug", formData.slug);
    blogData.append("content", formData.content);

    // Fix for keywords handling
    if (formData.keywords && formData.keywords.trim()) {
      const keywordsArray = formData.keywords.split(",").map((kw) => kw.trim());
      blogData.append("keywords", JSON.stringify(keywordsArray));
    } else {
      blogData.append("keywords", JSON.stringify([]));
    }

    if (formData.image) blogData.append("image", formData.image);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/postblog/create`,
        blogData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        alert("Blog post created successfully!");
        // Clear the draft after successful submission
        clearDraft();
        // Clear the draft after successful submission
        clearDraft();
      }
    } catch (error) {
      console.error("Error submitting blog: ", error);
      alert("Something went wrong! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  
  const tabStyle = {
    padding: "10px 20px",
    marginRight: "5px",
    background: "#f0f0f0",
    border: "none",
    borderRadius: "5px 5px 0 0",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  };

  const activeTabStyle = {
    ...tabStyle,
    background: "#2563eb",
    color: "white",
  };

  return (
    <DashboardLayout>
      <div style={{ minHeight: "100vh", backgroundColor: "#f3f4f6", padding: "0rem" }}>
        {/* Tab Navigation */}
        <div style={{ display: "flex", marginBottom: "1rem" }}>
          <button
            style={activeTab === "createPost" ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab("createPost")}
          >
            Create Post
          </button>
          <button
            style={activeTab === "management" ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab("management")}
          >
            Blog Management
          </button>
          <button
            style={activeTab === "subscribers" ? activeTabStyle : tabStyle}
            onClick={() => setActiveTab("subscribers")}
          >
            Subscribers List
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ backgroundColor: "white", borderRadius: "0 5px 5px 5px",  }}>
          {activeTab === "createPost" && (
            <div
              style={{
                display: "flex",
                alignItems: "center", 
                justifyContent: "center",
                backgroundColor: "#f3f4f6",
               
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  width: "100%",
                  maxWidth: "1000px",
                }}
              >
                <h1
                  style={{
                    fontSize: "1.875rem",
                    fontWeight: "600",
                    textAlign: "center",
                    color: "#1890ff",
                    marginBottom: "1.5rem",
                  }}
                >
                  Create a Blog Post
                </h1>

                {/* Draft Status Indicator */}
                <div style={{ marginBottom: "1rem", textAlign: "right" }}>
                  {draftExists && (
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", alignItems: "center" }}>
                      <span style={{ color: draftSaved ? "#059669" : "#6B7280", fontSize: "0.875rem" }}>
                        {draftSaved ? "Draft saved!" : "Draft loaded"}
                      </span>
                      <button
                        type="button"
                        onClick={clearDraft}
                        style={{
                          backgroundColor: "#ef4444",
                          color: "white",
                          padding: "0.3rem 0.5rem",
                          fontSize: "0.75rem",
                          borderRadius: "4px",
                          cursor: "pointer",
                          border: "none",
                        }}
                      >
                        Clear Draft
                      </button>
                    </div>
                  )}
                </div>

                <form
                  onSubmit={handleSubmit}
                  style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "1rem",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Author Name
                      </label>
                      <input
                        type="text"
                        name="authorName"
                        value={formData.authorName}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          padding: "0.5rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          marginTop: "0.25rem",
                        }}
                        placeholder="Enter author name"
                        required
                      />
                    </div>

                    <div>
                      <label
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#374151",
                        }}
                      >
                        Title
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        style={{
                          width: "100%",
                          padding: "0.5rem",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          marginTop: "0.25rem",
                        }}
                        placeholder="Enter post title"
                        required
                      />
                    </div>
                  </div>

                  {/* Meta fields section added here */}
                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Meta Title
                    </label>
                    <input
                      type="text"
                      name="metaTitle"
                      value={formData.metaTitle}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        marginTop: "0.25rem",
                      }}
                      placeholder="Enter meta title "
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Meta Description
                    </label>
                    <textarea
                      name="metaDescription"
                      value={formData.metaDescription}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        marginTop: "0.25rem",
                        minHeight: "80px",
                      }}
                      placeholder="Enter meta description"
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Slug
                    </label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        marginTop: "0.25rem",
                      }}
                      placeholder="URL-friendly slug (auto-generated from title)"
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Keywords
                    </label>
                    <input
                      type="text"
                      name="keywords"
                      value={formData.keywords}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        marginTop: "0.25rem",
                      }}
                      placeholder="Enter keywords separated by commas"
                    />
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        marginTop: "0.25rem",
                      }}
                      required
                    >
                      <option value="">Select a Category</option>
                      <option value="Unified events">Unified events</option>
                      <option value="HR & Job">HR & Job</option>
                      <option value="IT Technology">IT Technology</option>
                      <option value="BFSI">BFSI</option>
                      <option value="Education">Education</option>
                      <option value="Cyber Security">Cyber Security</option>
                      <option value="Contact Center ">Contact Center </option>
                      <option value="Sales Marketing">Sales Marketing</option>
                      <option value="Digital Marketing">Digital Marketing</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Image Upload
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ width: "100%", marginTop: "0.25rem" }}
                      onChange={handleImageChange}
                    />
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          marginTop: "0.5rem",
                          width: "100%",
                          height: "160px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Content
                    </label>
                    <ReactQuill
                      theme="snow"
                      value={formData.content}
                      onChange={handleContentChange}
                      style={{
                        backgroundColor: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        height: "240px",
                        marginTop: "0.25rem",
                      }}
                    />
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "#6b7280",
                        marginTop: "0.5rem",
                      }}
                    >
                      Word Count: {wordCount}
                    </p>
                  </div>

                  <button
                    type="submit"
                    style={{
                      width: "100%",
                      backgroundColor: "#2563eb",
                      color: "white",
                      padding: "0.75rem",
                      borderRadius: "6px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    {loading ? "Submitting..." : "Submit Post"}
                  </button>
                </form>
              </div>
            </div>
          )}

          {activeTab === "management" && (
            
            <BlogManagement />
          )}

          {activeTab === "subscribers" && (
          
            <SubscribersList />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PostBlog;